import { safeSetDoc, safeAddDoc } from '../utils';
import React, { useEffect, useState, useRef, memo, useMemo } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Extension, InputRule } from '@tiptap/core';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Pilcrow,
  Undo,
  Redo,
  Trash2,
  Check as CheckIcon,
  Link as LinkIcon,
  Type,
  WifiOff,
  RefreshCw,
  Image as ImageIcon,
  Book,
  FileDown,
  Music,
} from 'lucide-react';
import {
  query,
  collection,
  where,
  onSnapshot,
  doc,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../firebase';
import { appId } from '../constants';

// --- Custom Extensions ---
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addAttributes() {
    return {
      fontSize: {
        default: null,
        parseHTML: (element) => element.style.fontSize?.replace(/['"]+/g, ''),
        renderHTML: (attributes) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setAttributes('textStyle', { fontSize: null }).run(),
    };
  },
});

const CustomFontFamily = Extension.create({
  name: 'fontFamily',
  addOptions() {
    return { types: ['textStyle', 'heading'] };
  },
  addAttributes() {
    return {
      fontFamily: {
        default: null,
        parseHTML: (element) => element.style.fontFamily?.replace(/['"]+/g, ''),
        renderHTML: (attributes) => {
          if (!attributes.fontFamily) return {};
          return { style: `font-family: ${attributes.fontFamily}` };
        },
      },
    };
  },
  addCommands() {
    return {
      setFontFamily:
        (fontFamily) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontFamily }).run(),
      unsetFontFamily:
        () =>
        ({ chain }) =>
          chain().setAttributes('textStyle', { fontFamily: null }).run(),
    };
  },
});

const SmartEmojis = Extension.create({
  name: 'smartEmojis',
  addInputRules() {
    return [
      new InputRule({
        find: /<3\s$/,
        handler: ({ chain, range }) => chain().deleteRange(range).insertContent('❤️').run(),
      }),
      new InputRule({
        find: /:\)\s$/,
        handler: ({ chain, range }) => chain().deleteRange(range).insertContent('😊').run(),
      }),
      new InputRule({
        find: /:fire:\s$/,
        handler: ({ chain, range }) => chain().deleteRange(range).insertContent('🔥').run(),
      }),
      new InputRule({
        find: /:star:\s$/,
        handler: ({ range, chain }) => chain().deleteRange(range).insertContent('⭐').run(),
      }),
      new InputRule({
        find: /:check:\s$/,
        handler: ({ chain, range }) => chain().deleteRange(range).insertContent('✅').run(),
      }),
    ];
  },
});

const IMAGE_MIN_WIDTH = 60;

const IMAGE_RESIZE_CORNERS = [
  { id: 'top-left', horizontalDirection: -1, verticalDirection: -1, className: '-top-1.5 -left-1.5 cursor-nwse-resize' },
  { id: 'top-right', horizontalDirection: 1, verticalDirection: -1, className: '-top-1.5 -right-1.5 cursor-nesw-resize' },
  { id: 'bottom-left', horizontalDirection: -1, verticalDirection: 1, className: '-bottom-1.5 -left-1.5 cursor-nesw-resize' },
  { id: 'bottom-right', horizontalDirection: 1, verticalDirection: 1, className: '-bottom-1.5 -right-1.5 cursor-nwse-resize' },
];

const ImageComponent = ({ node, deleteNode, selected, updateAttributes, editor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [canMouseResize, setCanMouseResize] = useState(false);
  const imgRef = useRef(null);
  const wrapperRef = useRef(null);
  const dragCleanupRef = useRef(null);

  useEffect(() => {
    const mouseMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    const updateMouseResizeSupport = () => setCanMouseResize(mouseMedia.matches);

    updateMouseResizeSupport();
    mouseMedia.addEventListener?.('change', updateMouseResizeSupport);
    return () => mouseMedia.removeEventListener?.('change', updateMouseResizeSupport);
  }, []);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this image?')) {
      deleteNode();
    }
  };

  const handleResizeMouseDown = (e, corner) => {
    if (e.button !== 0 || !canMouseResize) return;

    e.preventDefault();
    e.stopPropagation();
    dragCleanupRef.current?.();

    const image = imgRef.current;
    const contentElement = wrapperRef.current?.closest('.ProseMirror') || wrapperRef.current?.parentElement;
    if (!image || !contentElement) return;

    const startWidth = image.clientWidth;
    const startHeight = image.clientHeight;
    if (!startWidth || !startHeight) return;

    const contentStyles = window.getComputedStyle(contentElement);
    const horizontalPadding =
      (parseFloat(contentStyles.paddingLeft) || 0) + (parseFloat(contentStyles.paddingRight) || 0);
    const availableWidth = Math.max(0, contentElement.clientWidth - horizontalPadding);
    const maxWidth = Math.max(1, Math.floor(availableWidth || startWidth));
    const minWidth = Math.min(IMAGE_MIN_WIDTH, maxWidth);
    const aspectRatio = startWidth / startHeight;
    const startX = e.clientX;
    const startY = e.clientY;
    let finalWidth = startWidth;
    let finalHeight = startHeight;
    let didResize = false;

    setIsResizing(true);

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = corner.className.includes('nwse') ? 'nwse-resize' : 'nesw-resize';

    const onMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const horizontalDelta = (moveEvent.clientX - startX) * corner.horizontalDirection;
      const verticalDelta = (moveEvent.clientY - startY) * corner.verticalDirection * aspectRatio;
      const widthDelta =
        Math.abs(horizontalDelta) >= Math.abs(verticalDelta) ? horizontalDelta : verticalDelta;

      finalWidth = Math.round(Math.min(maxWidth, Math.max(minWidth, startWidth + widthDelta)));
      finalHeight = Math.round(finalWidth / aspectRatio);
      didResize = finalWidth !== startWidth || finalHeight !== startHeight;

      // Force the style to update visually immediately without triggering Tiptap transaction loop
      if (imgRef.current) {
        imgRef.current.style.width = `${finalWidth}px`;
        imgRef.current.style.height = `${finalHeight}px`;
      }
    };

    const cleanupDrag = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      dragCleanupRef.current = null;
    };

    const onMouseUp = () => {
      cleanupDrag();
      setIsResizing(false);

      if (didResize) {
        // Keep this update in the existing inline HTML + debounced note-save path.
        editor.commands.focus(undefined, { scrollIntoView: false });
        requestAnimationFrame(() => {
          if (!editor.isDestroyed) {
            updateAttributes({ width: `${finalWidth}px`, height: `${finalHeight}px` });
          }
        });
      } else if (imgRef.current) {
        imgRef.current.style.width = node.attrs.width || 'auto';
        imgRef.current.style.height = 'auto';
      }
    };

    dragCleanupRef.current = cleanupDrag;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={`relative inline-block align-middle my-4 group/img ${selected ? 'ring-4 ring-blue-500 rounded-lg shadow-xl' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt}
        title={node.attrs.title}
        style={{
          width: node.attrs.width || 'auto',
          height: 'auto',
          maxWidth: '100%',
          display: 'block',
        }}
        className={`rounded-lg shadow-sm ${isResizing ? 'ring-2 ring-blue-400 transition-none' : 'transition-all duration-300'}`}
      />

      {(selected || isHovered || isResizing) && (
        <>
          <div className="absolute top-2 right-2 flex gap-2 animate-in fade-in zoom-in duration-200 z-[100]">
            <button
              onClick={handleDelete}
              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-2xl transition-all transform hover:scale-110 active:scale-95 border-2 border-white/20"
              title="Delete Image"
            >
              <Trash2 size={18} strokeWidth={2.5} />
            </button>
          </div>

          {canMouseResize && IMAGE_RESIZE_CORNERS.map((corner) => (
            <div
              key={corner.id}
              onMouseDown={(e) => handleResizeMouseDown(e, corner)}
              className={`absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-sm shadow-md hover:bg-blue-50 z-[110] ${corner.className}`}
              title="Resize image"
            />
          ))}
        </>
      )}
    </NodeViewWrapper>
  );
};

const FONT_FAMILIES = [
  { label: 'System', value: 'system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Mono', value: 'monospace' },
  { label: 'Classic', value: 'Times New Roman, serif' },
  { label: 'Modern', value: 'Helvetica, sans-serif' },
  { label: 'Wide', value: 'Verdana, sans-serif' },
  { label: 'Cursive', value: 'cursive' },
  { label: 'Geometric', value: 'Futura, sans-serif' },
  { label: 'Narrow', value: 'Arial Narrow, sans-serif' },
  { label: 'Soft', value: 'Trebuchet MS, sans-serif' },
];

const ChapterList = ({ editor }) => {
  const [chapters, setChapters] = useState([]);

  // Sync chapters on editor updates (structure changes)
  useEffect(() => {
    if (!editor) return;

    const updateChapters = () => {
      const items = [];
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.level === 1) {
          const text = node.textContent.trim();
          if (text) {
            items.push({ text, pos });
          }
        }
      });

      setChapters((prev) => {
        if (prev.length !== items.length) return items;
        const isDifferent = items.some(
          (item, i) => item.text !== prev[i].text || Math.abs(item.pos - prev[i].pos) > 5
        );
        return isDifferent ? items : prev;
      });
    };

    editor.on('update', updateChapters);
    updateChapters();
    return () => editor.off('update', updateChapters);
  }, [editor]);

  if (chapters.length === 0) return null;

  return (
    <div className="px-4 py-1 bg-white/95 backdrop-blur-md border-b border-zinc-100 flex flex-wrap overflow-y-auto custom-scrollbar content-start max-h-24 gap-1 sticky top-0 z-20 transition-all duration-300 print:hidden">
      {chapters.map((ch, i) => (
        <button
          key={i}
          onClick={() => {
            const el = editor.view.nodeDOM(ch.pos);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="text-[10px] font-bold text-zinc-400 hover:text-zinc-900 transition-all py-0.5 px-2.5 rounded-full hover:bg-zinc-50 border border-transparent hover:border-zinc-100 truncate max-w-[250px]"
        >
          {ch.text}
        </button>
      ))}
    </div>
  );
};

const NotepadView = (props) => {
  // Key by activeChannelId to force complete remount on project change
  return <NotepadEditor key={props.activeChannelId} {...props} />;
};

const NotepadEditor = ({
  path,
  user,
  currentView,
  activeColorHex,
  isSpellCheckEnabled,
  isSavingNote,
  dispatch,
  activeChannelId,
  noteTimeoutRef,
  isLocalEditRef,
  handleSaveNote,
  isMediaPlayerOpen,
  isMediaPlayerCompact,
  activeUploads,
  handleBackgroundUpload,
  b2Token,
}) => {
  const [noteId, setNoteId] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastSaved, setLastSaved] = useState(null);
  const [showChapterIndex, setShowChapterIndex] = useState(true);
  const [tracks, setTracks] = useState([]);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = Math.random().toString(36).substring(7);
  const uploadProgress = activeUploads?.[`note-${noteId}`];
  const isImageUploading = uploadProgress !== undefined;

  // Fetch project tracks for referencing
  useEffect(() => {
    if (!activeChannelId) return;
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'media'),
      where('channelId', '==', activeChannelId),
      where('type', '==', 'audio')
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setTracks(docs);
    });
    return () => unsub();
  }, [activeChannelId]);

  // Refs to capture current IDs for cleanup/unmount logic
  const currentNoteIdRef = useRef(null);

  const saveNote = async (html, targetNoteId) => {
    const nid = targetNoteId || currentNoteIdRef.current;
    if (!nid || !handleSaveNote) return;
    try {
      let cleanHtml = html;
      if (b2Token) {
        cleanHtml = cleanHtml.replace(new RegExp(`\\?Authorization=${b2Token}`, 'g'), '');
      }
      const result = await handleSaveNote(nid, cleanHtml, sessionIdRef.current);
      setLastSaved(new Date());
      return result;
    } catch (e) {
      console.error('Save failed:', e);
    }
  };

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2] },
      history: true,
      bulletList: false,
      orderedList: false,
      listItem: false,
      codeBlock: false,
      horizontalRule: false,
      link: false,
    }),
        Highlight.configure({ multicolor: true }),
        TextAlign.configure({ types: ['paragraph', 'heading', 'image'] }),
        TextStyle,
        FontSize,
        CustomFontFamily,
        Link.configure({
          openOnClick: false,
          protocols: ['track'],
          HTMLAttributes: {
            class: 'text-indigo-600 font-black underline cursor-pointer hover:text-indigo-800 decoration-2 underline-offset-2',
          },
        }),
        Placeholder.configure({ placeholder: 'Start typing your project notes here...' }),
        Image.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              width: {
                default: null,
                renderHTML: (attributes) => {
                  if (!attributes.width) return {};
                  return { style: `width: ${attributes.width}` };
                },
                parseHTML: (element) => element.style.width || element.getAttribute('width'),
              },
              height: {
                default: null,
                renderHTML: (attributes) => {
                  if (!attributes.height) return {};
                  return { style: `height: ${attributes.height}` };
                },
                parseHTML: (element) => element.style.height || element.getAttribute('height'),
              },
            };
          },
          addNodeView() {
            return ReactNodeViewRenderer(ImageComponent);
          },
        }).configure({
          inline: true,
        }),
      SmartEmojis,
  ], []);

  const editor = useEditor(
    {
      extensions,
      content: '',
      onTransaction: () => {
        // forceUpdate(); // Temporarily disabled to prevent infinite loop
      },
      editorProps: {
        attributes: {
          class: `prose prose-sm sm:prose lg:prose-lg max-w-none focus:outline-none min-h-[calc(100vh-120px)] px-10 pt-4 pb-12 bg-white transition-all duration-300 shadow-sm`,
          spellcheck: isSpellCheckEnabled ? 'true' : 'false',
        },
        handleKeyDown: (view, event) => {
          const { selection, doc } = view.state;
          if (event.key === 'Backspace' || event.key === 'Delete') {
            if (selection.node && selection.node.type.name === 'image') return true;
            const { $anchor } = selection;
            if (event.key === 'Backspace') {
              const before = $anchor.nodeBefore;
              if (before && before.type.name === 'image') return true;
              if ($anchor.parentOffset === 0) {
                const pos = $anchor.before();
                if (pos > 0) {
                  const nodeBefore = doc.nodeAt(pos - 1);
                  if (nodeBefore && nodeBefore.type.name === 'image') return true;
                }
              }
            }
            if (event.key === 'Delete') {
              const after = $anchor.nodeAfter;
              if (after && after.type.name === 'image') return true;
              if ($anchor.parentOffset === $anchor.parent.content.size) {
                const pos = $anchor.after();
                if (pos < doc.content.size) {
                  const nodeAfter = doc.nodeAt(pos);
                  if (nodeAfter && nodeAfter.type.name === 'image') return true;
                }
              }
            }
          }
          return false;
        },
        handleDOMEvents: {
          keydown: () => {
            isLocalEditRef.current = true;
            return false;
          },
        },
      },
      onUpdate: ({ editor }) => {
        if (editor.isFocused) isLocalEditRef.current = true;
        if (!isLocalEditRef.current) return;
        const html = editor.getHTML();
        if (noteTimeoutRef.current) clearTimeout(noteTimeoutRef.current);
        dispatch({ type: 'SET_SAVING_NOTE', payload: true });
        noteTimeoutRef.current = setTimeout(async () => {
          await saveNote(html);
          dispatch({ type: 'SET_SAVING_NOTE', payload: false });
          isLocalEditRef.current = false;
          noteTimeoutRef.current = null;
        }, 1000);
      },
    },
    [activeChannelId]
  );

  useEffect(() => {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (
      editor && isLocalHost &&
      (new URLSearchParams(window.location.search).get('access') === 'kx92j' ||
        window.__IS_VAULT_ACCESS)
    ) {
      window.notepadEditor = editor;
    }
  }, [editor]);

  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.destroy();
      }
    };
  }, [editor]);

  // Forced save on unmount (project change)
  useEffect(() => {
    return () => {
      if (isLocalEditRef.current && editor && currentNoteIdRef.current && handleSaveNote) {
        let html = editor.getHTML();
        if (b2Token) {
          html = html.replace(new RegExp(`\\?Authorization=${b2Token}`, 'g'), '');
        }
        safeSetDoc(
          doc(db, 'artifacts', appId, 'public', 'data', 'notes', currentNoteIdRef.current),
          {
            content: html,
            updatedAt: serverTimestamp(),
            updatedBy: user?.uid || 'test-uid-123',
            updatedBySession: sessionIdRef.current,
          },
          { merge: true }
        ).catch((e) => console.error('Final cleanup save failed:', e));
      }
      if (noteTimeoutRef.current) clearTimeout(noteTimeoutRef.current);
      isLocalEditRef.current = false;
    };
  }, [editor]);

  useEffect(() => {
    if (!activeChannelId) return;
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'notes'),
      where('channelId', '==', activeChannelId)
    );
    return onSnapshot(q, (snap) => {
      if (snap.empty) {
        const createNote = async () => {
          const docRef = await safeAddDoc(
            collection(db, 'artifacts', appId, 'public', 'data', 'notes'),
            {
              channelId: activeChannelId,
              content: '',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              updatedBy: user?.uid || 'test-uid-123',
            }
          );
          setNoteId(docRef.id);
          currentNoteIdRef.current = docRef.id;
        };
        createNote();
      } else {
        const doc = snap.docs[0];
        setNoteId(doc.id);
        currentNoteIdRef.current = doc.id;
        if (editor) {
          const currentContent = editor.getHTML();
          const data = doc.data();
          let newContent = data.content || '';
          newContent = newContent.replace(/f005\.backblazeb2\.com/g, 'f000.backblazeb2.com');
          if (b2Token) {
            newContent = newContent.replace(/(src="https:\/\/f000\.backblazeb2\.com[^"?]+)(?:")?([^>]*>)/g, `$1?Authorization=${b2Token}"$2`);
          }
          
          // Only update if content differs AND it was saved by a different session
          if (currentContent !== newContent && data.updatedBySession !== sessionIdRef.current) {
            // Crucial: Pass false as second argument to prevent this update from being added to history
            editor.commands.setContent(newContent, false);

            // Force clear history after loading remote content to ensure undo starts from now
            setTimeout(() => {
              if (editor && !editor.isDestroyed) {
                editor.undoManager?.clear();
              }
            }, 500);
          }
        }
      }
    });
  }, [activeChannelId, editor]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleImageUpload = async (file) => {
    if (!file || !noteId || !handleBackgroundUpload) return;
    
    handleBackgroundUpload(file, noteId, 'note', {
      uploadId: `note-${noteId}`,
      onComplete: (fileUrl) => {
        editor
          .chain()
          .focus()
          .insertContent({ type: 'image', attrs: { src: fileUrl } })
          .run();
      }
    });
  };

  const handleExportPDF = () => {
    // Get the project name from the path if available
    const projectName = path && path.length > 0 ? path[path.length - 1].name : 'My';
    const oldTitle = document.title;
    document.title = `${projectName} Notes`;
    window.print();
    // Use a slight delay to restore title to ensure the print dialog captures it
    setTimeout(() => {
      document.title = oldTitle;
    }, 100);
  };

  if (!editor) return null;

  return (
    <div data-testid="notepad-editor-container" className="flex flex-col h-full bg-zinc-50/50 print:h-auto print:bg-white">
      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white border-b border-zinc-200 px-4 py-2 flex items-center justify-between shadow-sm z-30 print:hidden">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-1">
          {path && (
            <div className="flex items-center gap-2 mr-4 pr-4 border-r border-zinc-100 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-widest font-black text-zinc-800">
                Notepad
              </span>
              <span className="text-[10px] text-zinc-400 font-normal tracking-normal truncate">
                NYTM{path.map((p) => ` > ${p.name}`).join('')}
              </span>
            </div>
          )}
          {/* Font & Size Selectors */}
          <div className="flex items-center gap-2 mr-2 pr-2 border-r border-zinc-100">
            <div className="relative flex items-center group">
              <Type
                size={14}
                className="absolute left-1.5 text-zinc-400 group-hover:text-zinc-900 transition-colors pointer-events-none"
              />
              <select
                data-testid="font-family-dropdown"
                className="bg-zinc-50 hover:bg-zinc-100 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-zinc-900 pl-6 pr-2 py-1.5 rounded-lg outline-none cursor-pointer transition-all border border-zinc-100 appearance-none min-w-[80px]"
                onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
                value={
                  FONT_FAMILIES.find((f) => editor.isActive('textStyle', { fontFamily: f.value }))
                    ?.value || ''
                }
              >
                <option value="">Default</option>
                {FONT_FAMILIES.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-0.5">
              <button
                data-testid="normal-text-btn"
                onClick={() => editor.chain().focus().setParagraph().run()}
                className={`p-1 rounded hover:bg-zinc-100 transition-colors ${editor.isActive('paragraph') ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
                title="Normal Text"
              >
                <Pilcrow size={16} strokeWidth={3} />
              </button>
              <button
                data-testid="h1-btn"
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                className={`p-1 rounded hover:bg-zinc-100 transition-colors ${editor.isActive('heading', { level: 1 }) ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
                title="Heading 1"
              >
                <Heading1 size={16} strokeWidth={3} />
              </button>
              <button
                data-testid="h2-btn"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className={`p-1 rounded hover:bg-zinc-100 transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
                title="Heading 2"
              >
                <Heading2 size={16} strokeWidth={3} />
              </button>
            </div>
          </div>

          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive('bold') ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
            title="Bold (Ctrl+B)"
          >
            <Bold size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive('italic') ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
            title="Italic (Ctrl+I)"
          >
            <Italic size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive('underline') ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
            title="Underline (Ctrl+U)"
          >
            <UnderlineIcon size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive('strike') ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
            title="Strikethrough"
          >
            <Strikethrough size={16} />
          </button>

          <div className="w-px h-4 bg-zinc-200 mx-1" />

          {tracks.length > 0 && (
            <>
              <div className="relative flex items-center group">
                <Music
                  size={16}
                  className="absolute left-1.5 text-zinc-400 group-hover:text-zinc-900 transition-colors pointer-events-none"
                />
                <select
                  title="Link Track"
                  className="bg-transparent text-transparent pl-7 pr-1 py-1.5 rounded-lg outline-none cursor-pointer transition-all appearance-none w-8 hover:bg-zinc-100"
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const track = tracks.find((t) => t.id === e.target.value);
                    if (track) {
                      editor
                        .chain()
                        .focus()
                        .insertContent(`🎵 ${track.title} `)
                        .setTextSelection({ from: editor.state.selection.from - track.title.length - 3, to: editor.state.selection.from - 1 })
                        .setLink({ href: `track:${track.id}` })
                        .unsetSelection()
                        .run();
                    }
                    e.target.value = '';
                  }}
                  value=""
                >
                  <option value="" disabled hidden></option>
                  {tracks.map((t) => (
                    <option key={t.id} value={t.id} className="text-zinc-900 text-[10px] font-black uppercase">
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-px h-4 bg-zinc-200 mx-1" />
            </>
          )}

          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive({ textAlign: 'left' }) ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
          >
            <AlignLeft size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive({ textAlign: 'center' }) ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
          >
            <AlignCenter size={16} />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-1.5 rounded hover:bg-zinc-100 ${editor.isActive({ textAlign: 'right' }) ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
          >
            <AlignRight size={16} />
          </button>

          <div className="w-px h-4 bg-zinc-200 mx-1" />

          <label className="p-1.5 rounded hover:bg-zinc-100 text-zinc-400 cursor-pointer flex items-center gap-1">
            <ImageIcon size={16} />
            {isImageUploading && (
              <span className="text-[9px] font-black text-blue-500 animate-pulse">
                {uploadProgress}%
              </span>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleImageUpload(e.target.files[0])}
              disabled={isImageUploading}
            />
          </label>

          <div className="w-px h-4 bg-zinc-200 mx-1" />

          <button
            data-testid="undo-btn"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className={`p-1.5 rounded hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed ${editor.can().undo() ? 'text-zinc-400 hover:text-zinc-900' : 'text-zinc-300'}`}
            title="Undo (Ctrl+Z)"
          >
            <Undo size={16} />
          </button>
          <button
            data-testid="redo-btn"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className={`p-1.5 rounded hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed ${editor.can().redo() ? 'text-zinc-400 hover:text-zinc-900' : 'text-zinc-300'}`}
            title="Redo (Ctrl+Y)"
          >
            <Redo size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowChapterIndex(!showChapterIndex)}
            className={`p-1.5 rounded hover:bg-zinc-100 transition-colors ${showChapterIndex ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
            title="Toggle Chapter Index"
          >
            <Book size={16} />
          </button>
          <div className="flex items-center gap-1 border-r border-zinc-100 pr-3 mr-1">

            <button
              onClick={() => dispatch({ type: 'TOGGLE_MEDIA_PLAYER' })}
              className={`p-1.5 rounded hover:bg-zinc-100 transition-colors ${isMediaPlayerOpen ? 'bg-zinc-100 text-zinc-900 font-bold' : 'text-zinc-400'}`}
              title="Toggle Media Player"
            >
              <Music size={16} />
            </button>
          </div>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1 px-2 py-1 bg-zinc-900 text-white rounded hover:bg-black transition-colors"
            title="Export as PDF"
          >
            <FileDown size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">PDF</span>
          </button>

          {isOffline && (
            <div className="flex items-center gap-1 text-[10px] font-bold text-red-500 uppercase tracking-widest bg-red-50 px-2 py-1 rounded-full border border-red-100">
              <WifiOff size={10} />
              <span>Offline</span>
            </div>
          )}
          {isSavingNote ? (
            <div className="flex items-center gap-1.5 text-[10px] font-black text-blue-500 uppercase tracking-widest animate-pulse">
              <RefreshCw size={10} className="animate-spin" />
              <span>Saving</span>
            </div>
          ) : (
            lastSaved && (
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                <CheckIcon size={10} className="text-green-500" />
                <span>
                  Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      {/* Editor Area */}
      <div
        className="flex-1 overflow-y-auto custom-scrollbar bg-zinc-50 print:h-auto print:overflow-visible print:bg-white"
        onClick={(e) => {
          const target = e.target.closest('a');
          if (target && target.getAttribute('href')?.startsWith('track:')) {
            e.preventDefault();
            const trackId = target.getAttribute('href').split('track:')[1];
            window.dispatchEvent(new CustomEvent('play-track', { detail: trackId }));
            dispatch({ type: 'TOGGLE_MEDIA_PLAYER', payload: true });
            dispatch({ type: 'TOGGLE_MEDIA_PLAYER_COMPACT', payload: true });
          }
        }}
      >
        <div
          className="min-h-full max-w-4xl mx-auto bg-white shadow-sm border-x border-zinc-200/50"
          style={{
            fontFamily:
              editor.getAttributes('textStyle').fontFamily ||
              editor.getAttributes('heading').fontFamily ||
              '',
          }}
        >
          {showChapterIndex && <ChapterList editor={editor} />}
          <EditorContent key={activeChannelId} editor={editor} />
        </div>
      </div>

      {/* Bubble Menu */}
      {editor && (
        <BubbleMenu
          editor={editor}
          tippyoptions={{ duration: 100 }}
          shouldShow={({ editor, from, to, state }) => {
            if (state.selection.node && state.selection.node.type.name === 'image') return false;
            if (editor.isActive('image')) return false;
            return from !== to;
          }}
          className="flex bg-zinc-900 text-white rounded-lg shadow-xl overflow-hidden divide-x divide-white/10 p-1 border border-white/20 print:hidden"
        >
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 hover:bg-white/10 transition-colors ${editor.isActive('bold') ? 'text-blue-400' : 'text-white'}`}
          >
            <Bold size={14} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 hover:bg-white/10 transition-colors ${editor.isActive('italic') ? 'text-blue-400' : 'text-white'}`}
          >
            <Italic size={14} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 hover:bg-white/10 transition-colors ${editor.isActive('underline') ? 'text-blue-400' : 'text-white'}`}
          >
            <UnderlineIcon size={14} />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-1.5 hover:bg-white/10 transition-colors ${editor.isActive('strike') ? 'text-blue-400' : 'text-white'}`}
          >
            <Strikethrough size={14} />
          </button>
          <button
            onClick={() => {
              const url = window.prompt('URL');
              if (url) editor.chain().focus().setLink({ href: url }).run();
            }}
            className={`p-1.5 hover:bg-white/10 transition-colors ${editor.isActive('link') ? 'text-blue-400' : 'text-white'}`}
          >
            <LinkIcon size={14} />
          </button>
        </BubbleMenu>
      )}
    </div>
  );
};

export default memo(NotepadView);
